// region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Cluster API form
//
//  Copyright:    © 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
// region Version 1.0.0.0
//    001   12.03.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   05.02.26 Sean Flook         GMSCM-12 Changed EditDialog to ActionDialog.
// endregion Version 1.0.0.0
//
//--------------------------------------------------------------------------------------------------
// endregion header

import React, { useEffect, useState } from "react";

import { UserApis } from "../dtos/multiTenantClusterDto";
import ClusterErrorType from "../models/clusterErrorType";

import { Box, Grid2 } from "@mui/material";
import ADSTextControl from "../components/ADSTextControl";
import { dataFormStyle, FormRowStyle } from "../utils/ADSStyles";
import useUserQueryStore from "../context/UserStore";

interface ClusterApiFormProps {
  data: UserApis;
  errors: ClusterErrorType[] | undefined;
  onDataChange: (data: UserApis) => void;
}

const ClusterApiForm: React.FC<ClusterApiFormProps> = ({ data, errors, onDataChange }) => {
  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);
  const [iManage, setIManage] = useState<string | undefined>();
  const [settings, setSettings] = useState<string | undefined>();
  const [lookups, setLookups] = useState<string | undefined>();
  const [iManageError, setIManageError] = useState<string[] | undefined>();
  const [settingsError, setSettingsError] = useState<string[] | undefined>();
  const [lookupsError, setLookupsError] = useState<string[] | undefined>();

  /**
   * Method to update the data.
   * @param fieldName The field name to update.
   * @param value The new value.
   */
  const updateData = (fieldName: string, value: string | undefined): void => {
    const updatedApi = {
      ...data,
      [fieldName]: value,
    };

    if (onDataChange) {
      onDataChange(updatedApi);
    }
  };

  const handleIManageChangeEvent = (newValue: string): void => {
    setIManage(newValue);
    updateData("iManage", newValue);
  };

  const handleSettingsChangeEvent = (newValue: string): void => {
    setSettings(newValue);
    updateData("Settings", newValue);
  };

  const handleLookupsChangeEvent = (newValue: string): void => {
    setLookups(newValue);
    updateData("Lookups", newValue);
  };

  useEffect(() => {
    if (data) {
      setIManage(data.iManage);
      setSettings(data.Settings);
      setLookups(data.Lookups);
    }
  }, [data]);

  useEffect(() => {
    setIManageError(errors?.find((x) => x.field === "imanage")?.errors);
    setSettingsError(errors?.find((x) => x.field === "settings")?.errors);
    setLookupsError(errors?.find((x) => x.field === "lookups")?.errors);
  }, [errors]);

  return (
    <Box sx={dataFormStyle("ClusterForm")}>
      <Grid2 container justifyContent="flex-start" alignItems="baseline" sx={FormRowStyle()} rowSpacing={2}>
        <Grid2 size={12}>
          <ADSTextControl
            label="iManage"
            value={iManage}
            maxLength={500}
            id="cluster_api_imanage"
            isEditable={currentUser?.isSuperAdministrator}
            helperText="URL for iManage API."
            errorText={iManageError}
            onChange={handleIManageChangeEvent}
          />
          <ADSTextControl
            label="Settings"
            value={settings}
            maxLength={500}
            id="cluster_api_settings"
            isEditable={currentUser?.isSuperAdministrator}
            helperText="URL for Settings API."
            errorText={settingsError}
            onChange={handleSettingsChangeEvent}
          />
          <ADSTextControl
            label="Lookups"
            value={lookups}
            maxLength={500}
            id="cluster_api_lookups"
            isEditable={currentUser?.isSuperAdministrator}
            helperText="URL for Lookups API."
            errorText={lookupsError}
            onChange={handleLookupsChangeEvent}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default ClusterApiForm;
